/**
 * OpsKaizenBenchmarkPanel — Inteligência industrial / engenharia Kaizen
 * Integrado na aba Análise do Centro Operacional.
 * Sub-navegação controlada pelo ribbon superior (sem barra secundária).
 */
import React from 'react';
import { OpsKaizenSystemStatus } from './kaizen/OpsKaizenSystemStatus';
import { OpsKaizenValueFlowTab } from './kaizen/OpsKaizenValueFlowTab';
import { OpsKaizenLossesTab } from './kaizen/OpsKaizenLossesTab';
import { OpsKaizenFinancialTab } from './kaizen/OpsKaizenFinancialTab';
import { useOpsKaizenData } from './kaizen/useOpsKaizenData';
import { KAIZEN_GAP, KAIZEN_VIEWPORT_CLASS, type KaizenSubTab } from './kaizen/opsKaizenShared';

export interface OpsKaizenBenchmarkPanelProps {
  organizationId: string;
  periodDays: number;
  periodLabel: string;
  deptLabel?: string | null;
  subTab: KaizenSubTab;
  contextTitle?: string;
  contextQuestion?: string;
  onOpenBottlenecks?: () => void;
  focusStage?: string | null;
  focusWip?: number | null;
  focusMinutes?: number | null;
  onRegisterProblem?: () => void;
  onDismissFocus?: () => void;
}

export const OpsKaizenBenchmarkPanel: React.FC<OpsKaizenBenchmarkPanelProps> = ({
  organizationId,
  periodDays,
  periodLabel,
  deptLabel,
  subTab,
  contextTitle = 'Fluxo',
  contextQuestion,
  onOpenBottlenecks,
  focusStage = null,
  focusWip = null,
  focusMinutes = null,
  onRegisterProblem,
  onDismissFocus,
}) => {
  const data = useOpsKaizenData({ organizationId, periodDays });

  return (
    <div className={`flex ${KAIZEN_VIEWPORT_CLASS} flex-1 flex-col ${KAIZEN_GAP}`}>
      <div className="flex shrink-0 flex-wrap items-center gap-2 text-[10px] text-[var(--text-secondary)]">
        <span className="font-medium text-[var(--text-primary)]">{contextTitle}</span>
        {contextQuestion ? (
          <>
            <span className="text-[var(--border-default)]">·</span>
            <span className="font-semibold text-[var(--text-primary)]">{contextQuestion}</span>
          </>
        ) : null}
        {subTab === 'financial' ? (
          <span>Impacto anual cadastrado. O período do ribbon não recorta este total.</span>
        ) : (
          <>
            <span className="text-[var(--border-default)]">·</span>
            <span>
              Período: <span className="font-semibold text-[var(--text-primary)]">{periodLabel}</span>
            </span>
          </>
        )}
        {subTab === 'financial' && deptLabel ? (
          <span>O departamento selecionado não filtra estes benefícios.</span>
        ) : null}
        {subTab !== 'financial' && deptLabel ? (
          <>
            <span className="text-[var(--border-default)]">·</span>
            <span>
              Unidade: <span className="font-semibold text-[var(--text-primary)]">{deptLabel}</span>
            </span>
          </>
        ) : null}
      </div>

      {subTab === 'value-flow' && <OpsKaizenSystemStatus data={data} />}

      <div role="tabpanel" className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {subTab === 'value-flow' && (
          <OpsKaizenValueFlowTab data={data} onOpenBottlenecks={onOpenBottlenecks} />
        )}
        {subTab === 'losses' && (
          <OpsKaizenLossesTab
            data={data}
            focusStage={focusStage}
            focusWip={focusWip}
            focusMinutes={focusMinutes}
            onRegisterProblem={onRegisterProblem}
            onDismissFocus={onDismissFocus}
          />
        )}
        {subTab === 'financial' && <OpsKaizenFinancialTab data={data} periodLabel={periodLabel} />}
      </div>
    </div>
  );
};

export default OpsKaizenBenchmarkPanel;
